import { useCallback, useEffect, useState } from 'react';
import { useAiAgent } from '@/contexts/AiAgentContext';
import type { AiSelectionContext } from '@/utils/aiPageContextFromPath';

/**
 * Sync Ant Design Table rowSelection into ERP Assistant / UDQ page context.
 */
export function useAiListSelection(opts: {
  entityType: string;
  module?: string;
  listFilters?: Record<string, unknown>;
  labelById?: (id: string) => string | undefined;
}) {
  const { setAiSelection } = useAiAgent();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const sync = useCallback(
    (keys: React.Key[]) => {
      setSelectedRowKeys(keys);
      const ids = keys.map((k) => String(k)).filter(Boolean);
      if (!ids.length) {
        setAiSelection(null);
        return;
      }
      const sel: AiSelectionContext = {
        entityType: opts.entityType,
        ids,
        labels: opts.labelById ? ids.map((id) => opts.labelById!(id)).filter(Boolean) as string[] : undefined,
        listFilters: opts.listFilters,
      };
      setAiSelection(sel);
    },
    [opts.entityType, opts.labelById, opts.listFilters, setAiSelection]
  );

  useEffect(() => {
    return () => setAiSelection(null);
  }, [setAiSelection]);

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[]) => sync(keys),
  };

  return { selectedRowKeys, setSelectedRowKeys: sync, rowSelection, selectionCount: selectedRowKeys.length };
}
